import {useFormikContext} from 'formik';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import Select from 'react-select/creatable';
import type {SingleValue} from 'react-select';
import {Appliance} from 'components/customer/Product/Appliance/entity/Appliance';
import {CustomDropdownIndicator} from 'components/customer/Product/Appliance/CustomDropdown';
import {useSelectStyle} from 'components/customer/Product/Appliance/helpers/useSelectStyle';

interface Make {
    label: string;
    value: string;
}

interface Props {
    makes: Make[];
}

export const CustomSelectWithBlur = ({makes}: Props) => {
    const {style, setError} = useSelectStyle();
    const {values, errors, setFieldValue} = useFormikContext<Appliance>();

    const [inputValue, setInputValue] = useState('');
    const justSelectedRef = useRef(false);

    const filterOption = useCallback(
        (
            option: {label: string; value: string; data: {label: string}},
            inputValue: string
        ) => {
            if (!inputValue.trim()) return false;
            return option.label
                .toLowerCase()
                .includes(inputValue.toLowerCase());
        },
        []
    );

    const fauxCallback = useCallback(() => null, []);

    const formatCreateLabel = useCallback(
        (input: string) => {
            const hasMatches = makes.some((make) =>
                make.label.toLowerCase().includes(input.toLowerCase().trim())
            );

            if (hasMatches) return `Click here to add new brand "${input}"`;
            return `Hit Enter or click here to add new brand "${input}"`;
        },
        [makes]
    );

    const onChangeHandler = useCallback(
        (option: SingleValue<Make>) => {
            if (option) {
                justSelectedRef.current = true;
                void setFieldValue('make', option.value);
            }
        },
        [setFieldValue]
    );

    const persistLocalValue = useCallback(() => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
        }

        const trimmed = inputValue.trim();
        if (trimmed) {
            void setFieldValue('make', trimmed);
        }
    }, [inputValue, setFieldValue]);

    const inputChangeHandler = useCallback((value: string) => {
        setInputValue(value);
    }, []);

    const focusHandler = useCallback(() => {
        setInputValue(values.make ?? '');
    }, [values.make]);

    useEffect(() => {
        setError(errors.make != undefined);
    }, [errors.make]);

    return (
        <Select
            inputId="appliance-brand"
            blurInputOnSelect
            filterOption={filterOption}
            noOptionsMessage={fauxCallback}
            placeholder="Select or add new brand"
            styles={{
                ...style,
                placeholder: (baseProps) => ({
                    ...baseProps,
                    fontSize: '1.2em',
                    color: '#585150',
                }),
                singleValue: (baseProps) => ({
                    ...baseProps,
                    fontSize: '1.2em',
                    color: '#585150',
                }),
                input: (baseProps) => ({
                    ...baseProps,
                    fontSize: '1.2em',
                    // do not remove this, this is required for focus/blur
                    // brand selection actions.
                    // react-select sizes the input's grid column to fit
                    // only its current text ('0 min-content').
                    // that leaves the rest of the "field" as dead click-space
                    // instead of the input. Clicking there causes all
                    // sort of problems. this fixes that by claiming all the available
                    // width as input field
                    gridTemplateColumns: '0 1fr',
                    cursor: 'text',
                }),
            }}
            options={makes}
            value={
                values.make
                    ? {
                          label: values.make,
                          value: values.make,
                      }
                    : null
            }
            onChange={onChangeHandler}
            formatCreateLabel={formatCreateLabel}
            components={{
                DropdownIndicator: CustomDropdownIndicator,
            }}
            inputValue={inputValue}
            onInputChange={inputChangeHandler}
            onFocus={focusHandler}
            onBlur={persistLocalValue}
        />
    );
};
